import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useAppSelector } from '../../redux/hooks';
import { selectCurrentLanguage, selectTourLanguage } from '../../redux/selectors';
import type { Place } from '../../types/place';
import ReactPlayer from 'react-player/lazy';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { IconButton } from '@mui/material';
import logoSvg from '../../assets/Xsite.svg';
import pauseController from '../../assets/pause-controller.svg';
import playController from '../../assets/play-controller.svg';
import stopController from '../../assets/stop-controller.svg';
import useScreenOrientation from '../../hooks/useOrientation';
import { useFullscreen } from '../../hooks/useFullscreen';
import { trackVideoPlayed, trackVideoFinished } from '../../mixpanel';
import { getPlace, createImageUrl } from '../../services/data';
import { isMediaPlayAbortError } from '../../utils/errorHandler';
import styles from './TodVideo.module.css';
import '../../styles/common/animations.css';

function formatTime(seconds: number): string {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
}

interface TodVideoPageProps {
  station?: Place;
}

const TodVideoPage: React.FC<TodVideoPageProps> = ({ station: stationProp }) => {
  const videoPlayerRef = useRef<ReactPlayer | null>(null);
  const playerContainerRef = useRef<HTMLDivElement | null>(null);
  const videoElement = useRef<HTMLVideoElement | null>(null);

  const [fullStation, setFullStation] = useState<Place | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  const navigate = useNavigate();
  const { placeId } = useParams();
  const { t } = useTranslation();
  const orientation = useScreenOrientation();
  const currentLanguage = useAppSelector(selectCurrentLanguage);
  const tourLanguage = useAppSelector(selectTourLanguage);
  const orientationRef = useRef<number>(window.orientation as number);
  const { isFullscreen: isFullScreen, toggle: toggleFullScreen } = useFullscreen(playerContainerRef, videoElement);

  useEffect(() => {
    const fetchFullData = async () => {
      if (!placeId) return;
      const place = await getPlace(placeId, currentLanguage ?? 'en', tourLanguage ?? undefined);
      setFullStation(place);
    };
    fetchFullData();
  }, [placeId, currentLanguage, tourLanguage]);

  const station = fullStation || stationProp;
  const videoTranslation = station?.translations?.[0]?.video;
  const videoUrl = videoTranslation?.id
    ? createImageUrl(videoTranslation.id)
    : null;

  const stationName = station?.translations?.[0]?.information_title || '';

  useEffect(() => {
    const handleOrientationChange = () => {
      orientationRef.current = window.orientation as number;
    };
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, []);


  const isLandscape = orientationRef.current === 0 || orientationRef.current === 180;

  const handlePlay = () => {
    trackVideoPlayed(stationName || 'TOD Video');
    setIsPlaying(true);
  };
  const handlePause = () => {
    setIsPlaying(false);
  };
  const handleStop = () => {
    setIsPlaying(false);
    setProgress(0);
    videoPlayerRef.current?.seekTo(0);
  };

  const progressHandler = (state: { playedSeconds: number }) => setProgress(state.playedSeconds);
  const seekHandler = (e: React.FormEvent<HTMLInputElement>) => {
    const value = Number((e.target as HTMLInputElement).value);
    setProgress(value);
    videoPlayerRef.current?.seekTo(value);
  };

  const handleReady = () => {
    videoElement.current = (videoPlayerRef.current?.getInternalPlayer() as HTMLVideoElement) ?? null;
    setProgress(0);
    setTimeout(() => {
      setIsLoading(false);
      setIsPlaying(true);
    }, 2000);
  };

  const handleError = (err: unknown, _data?: unknown, _hlsInstance?: unknown, _hlsGlobal?: unknown) => {
    // A play() request interrupted by pause()/unmount is a benign race —
    // don't report it or show the error overlay; the player will reconcile.
    if (isMediaPlayAbortError(err)) return;

    let mediaError: MediaError | null = null;
    if (err instanceof Event) {
      const target = (err as Event).target;
      if (target instanceof HTMLMediaElement) {
        mediaError = target.error;
      }
    }

    const actualError = mediaError ?? (err instanceof Error ? err : null);

    const isNotSupported =
      (actualError instanceof DOMException && actualError.name === 'NotSupportedError') ||
      (mediaError?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED);

    if (isNotSupported) {
      setHasError(true);
      setIsLoading(false);
      setIsPlaying(false);
      return;
    }

    const context = {
      videoUrl: videoUrl ?? 'unknown',
      stationName,
      ...(mediaError && { mediaErrorCode: mediaError.code, mediaErrorMessage: mediaError.message }),
    };

    if (actualError) {
      Sentry.captureException(actualError, { extra: context });
    } else {
      Sentry.captureMessage('Video playback error', { level: 'error', extra: context });
    }

    setHasError(true);
    setIsLoading(false);
    setIsPlaying(false);
  };

  const handleEnd = () => {
    trackVideoFinished(stationName || 'TOD Video');
    videoPlayerRef.current?.seekTo(0);
    setIsPlaying(false);
  };

  if (!videoUrl) {
    return <div style={{ color: 'white', padding: '2rem', textAlign: 'center' }}>No video available</div>;
  }

  return (
    <div className={`${styles['tod-video-player-container']} ${isLandscape ? '' : styles.landscape}`} ref={playerContainerRef}>
      <div className={`${styles['tod-player-wrapper']} ${isLandscape ? '' : styles.landscape}`}>
        <button className={styles['tod-floating-btn']} onClick={() => navigate(-1)}>
          {orientation === 'landscape' ? stationName : 'Back'}
        </button>
        {hasError && (
          <div className={styles['tod-error-overlay']}>
            <ErrorOutlineIcon sx={{ color: 'white', fontSize: 40 }} />
            <p style={{ color: 'white', fontFamily: 'Poppins, sans-serif', fontSize: '14px', marginTop: '8px' }}>
              {t('common.video-unavailable')}
            </p>
          </div>
        )}
        {isLoading && !hasError && (
          <div className={styles['tod-loader-overlay']}>
            <img src={logoSvg} alt="xsite-logo" className="spin-pulse" />
          </div>
        )}
        <ReactPlayer
          className={styles['tod-player']}
          ref={videoPlayerRef}
          url={videoUrl}
          width="100%"
          height="100%"
          controls={false}
          playing={isPlaying}
          onProgress={progressHandler}
          onReady={handleReady}
          onDuration={setDuration}
          muted={true}
          onEnded={handleEnd}
          onError={handleError}
          playsinline={true}
        />
      </div>
      <div className={styles['tod-control-container']} style={isFullScreen ? { width: '90%' } : {}}>
        <div className={styles['tod-middle-container']}>
          <div className={styles['tod-slider-container']}>
            <input
              type="range"
              className="time-bar"
              min="0"
              max={duration}
              value={progress}
              onInput={seekHandler}
            />
          </div>
          <div className={styles['tod-control-time-container']}>
            <div className={styles['tod-inner-controllers']}>
              <button className={styles['tod-icon-btn']} onClick={handlePlay}>
                <img src={playController} alt="play" />
              </button>
              <button className={styles['tod-icon-btn']} onClick={handlePause}>
                <img src={pauseController} alt="pause" />
              </button>
              <button className={styles['tod-icon-btn']} onClick={handleStop}>
                <img src={stopController} alt="stop" />
              </button>
            </div>
            <p>{formatTime(progress)}/{formatTime(duration)}</p>
            <IconButton sx={{ color: 'white' }} onClick={toggleFullScreen}>
              {isFullScreen ? <FullscreenExitIcon fontSize="large" /> : <FullscreenIcon fontSize="large" />}
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodVideoPage;
